import React, { useState, useCallback, useMemo } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  VideoCameraIcon,
  MapPinIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { Appointment, BookingSettings } from '../../types/appointments';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { useLanguage } from '../../contexts/LanguageContext';
import AppointmentEditModal from './AppointmentEditModal';

interface AppointmentListProps {
  appointments: Appointment[];
  settings: BookingSettings[];
  onUpdateStatus: (appointmentId: string, status: Appointment['status']) => void;
  onDeleteAppointment: (appointmentId: string) => void;
  onUpdateAppointment?: (updatedAppointment: Appointment) => void;
  loading?: boolean;
}

type FilterStatus = 'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed';
type SortField = 'date' | 'status' | 'client' | 'created';
type SortDirection = 'asc' | 'desc';

const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  settings,
  onUpdateStatus,
  onDeleteAppointment,
  onUpdateAppointment,
  loading = false
}) => {
  const { t } = useLanguage();
  
  // State
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Szűrés és rendezés
  const filteredAndSortedAppointments = useMemo(() => {
    let filtered = appointments;

    // Státusz szűrés
    if (filterStatus !== 'all') {
      filtered = filtered.filter(apt => apt.status === filterStatus);
    }

    // Keresés
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(apt =>
        apt.clientName.toLowerCase().includes(term) ||
        apt.clientEmail.toLowerCase().includes(term) ||
        apt.clientPhone?.toLowerCase().includes(term)
      );
    }

    // Rendezés
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'date':
          aValue = a.appointmentDate.getTime();
          bValue = b.appointmentDate.getTime();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'client':
          aValue = a.clientName.toLowerCase();
          bValue = b.clientName.toLowerCase();
          break;
        case 'created':
          aValue = a.createdAt?.getTime() || 0;
          bValue = b.createdAt?.getTime() || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [appointments, filterStatus, searchTerm, sortField, sortDirection]);

  // Státusz színek és ikonok
  const getStatusConfig = (status: Appointment['status']) => {
    switch (status) {
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
          icon: ExclamationTriangleIcon,
          label: 'Függőben'
        };
      case 'confirmed':
        return {
          color: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
          icon: CheckCircleIcon,
          label: 'Megerősítve'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
          icon: XCircleIcon,
          label: 'Lemondva'
        };
      case 'completed':
        return {
          color: 'bg-primary-50 text-primary-800 border-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:border-primary-500/30',
          icon: CheckCircleIcon,
          label: 'Befejezve'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800',
          icon: ClockIcon,
          label: 'Ismeretlen'
        };
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailsModal(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowEditModal(true);
  };

  const handleSaveAppointment = (updatedAppointment: Appointment) => {
    if (onUpdateAppointment) {
      onUpdateAppointment(updatedAppointment);
    }
    setShowEditModal(false);
    setSelectedAppointment(null);
  };

  const getSettingsTitle = (settingsId: string) => {
    const setting = settings.find(s => s.id === settingsId);
    return setting?.title || 'Ismeretlen szolgáltatás';
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fejléc és szűrők */}
      <div className="crm-card p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {/* Keresés */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg"
                placeholder="Keresés név, email vagy telefon alapján..."
              />
            </div>
          </div>

          {/* Szűrők gomb */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="h-9 sm:ml-auto"
          >
            <FunnelIcon className="h-4 w-4" />
            <span>Szűrők</span>
            {showFilters ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Szűrő panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-[var(--crm-border)]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Státusz szűrő */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Státusz
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                  className="w-full h-9 px-3 text-sm border rounded-lg"
                >
                  <option value="all">Összes</option>
                  <option value="pending">Függőben</option>
                  <option value="confirmed">Megerősítve</option>
                  <option value="completed">Befejezve</option>
                  <option value="cancelled">Lemondva</option>
                </select>
              </div>

              {/* Rendezés mező */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Rendezés
                </label>
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                  className="w-full h-9 px-3 text-sm border rounded-lg"
                >
                  <option value="date">Időpont</option>
                  <option value="status">Státusz</option>
                  <option value="client">Ügyfél név</option>
                  <option value="created">Létrehozás dátuma</option>
                </select>
              </div>

              {/* Rendezés irány */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Irány
                </label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value as SortDirection)}
                  className="w-full h-9 px-3 text-sm border rounded-lg"
                >
                  <option value="asc">Növekvő</option>
                  <option value="desc">Csökkenő</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Foglalások listája */}
      <div className="crm-card overflow-hidden">
        {filteredAndSortedAppointments.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDaysIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nincs találat
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all'
                ? 'Próbálj meg más keresési feltételeket.'
                : 'Még nincsenek foglalások.'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--crm-border)]">
            {filteredAndSortedAppointments.map((appointment) => {
              const statusConfig = getStatusConfig(appointment.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={appointment.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        {/* Státusz ikon */}
                        <div className={`hidden sm:flex p-2 rounded-full border flex-shrink-0 ${statusConfig.color}`}>
                          <StatusIcon className="h-4 w-4" />
                        </div>

                        {/* Fő információk */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 min-w-0">
                            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50 truncate">
                              {appointment.clientName}
                            </h3>
                            <span className={`
                              inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                              ${statusConfig.color}
                            `}>
                              {statusConfig.label}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-gray-600 dark:text-gray-400">
                            {/* Időpont */}
                            <div className="flex items-center space-x-2">
                              <CalendarDaysIcon className="h-4 w-4" />
                              <span>
                                {appointment.appointmentDate.toLocaleDateString('hu-HU')}
                              </span>
                            </div>

                            {/* Idő */}
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="h-4 w-4" />
                              <span>
                                {appointment.appointmentDate.toLocaleTimeString('hu-HU', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })} ({appointment.duration} perc)
                              </span>
                            </div>

                            {/* Email */}
                            <div className="flex items-center space-x-2">
                              <EnvelopeIcon className="h-4 w-4" />
                              <span className="truncate">{appointment.clientEmail}</span>
                            </div>

                            {/* Szolgáltatás */}
                            <div className="flex items-center space-x-2">
                              <UserIcon className="h-4 w-4" />
                              <span className="truncate">{getSettingsTitle(appointment.bookingSettingsId)}</span>
                            </div>
                          </div>

                          {/* Meeting link (ha van) */}
                          {appointment.meetingLink && (
                            <div className="mt-3 flex items-center space-x-2 text-sm">
                              <VideoCameraIcon className="h-4 w-4 text-green-600" />
                              <a
                                href={appointment.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 font-medium"
                              >
                                Online meeting link
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Akció gombok */}
                    <div className="flex flex-wrap items-center gap-1.5 lg:ml-4 lg:flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(appointment)}
                        className="flex items-center space-x-1"
                      >
                        <EyeIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Részletek</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditAppointment(appointment)}
                        className="flex items-center space-x-1"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Szerkesztés</span>
                      </Button>

                      {appointment.status === 'pending' && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onUpdateStatus(appointment.id!, 'confirmed')}
                            className="flex items-center space-x-1"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Jóváhagyás</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onUpdateStatus(appointment.id!, 'cancelled')}
                            className="flex items-center space-x-1 text-red-700 dark:text-red-400"
                          >
                            <XCircleIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Elutasítás</span>
                          </Button>
                        </>
                      )}

                      {appointment.status === 'confirmed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateStatus(appointment.id!, 'completed')}
                          className="flex items-center space-x-1 text-primary-700 dark:text-primary-300"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">Befejezés</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Részletek modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Foglalás részletei"
        size="lg"
      >
        {selectedAppointment && (
          <div className="p-6 space-y-6">
            {/* Ügyfél információk */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                Ügyfél információk
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">{selectedAppointment.clientName}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                  <span>{selectedAppointment.clientEmail}</span>
                </div>
                {selectedAppointment.clientPhone && (
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="h-4 w-4 text-gray-500" />
                    <span>{selectedAppointment.clientPhone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Időpont információk */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                Időpont részletei
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                  <span>
                    {selectedAppointment.appointmentDate.toLocaleDateString('hu-HU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-4 w-4 text-blue-600" />
                  <span>
                    {selectedAppointment.appointmentDate.toLocaleTimeString('hu-HU', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })} - {new Date(selectedAppointment.appointmentDate.getTime() + selectedAppointment.duration * 60000).toLocaleTimeString('hu-HU', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-4 w-4 text-blue-600" />
                  <span>{getSettingsTitle(selectedAppointment.bookingSettingsId)}</span>
                </div>
              </div>
            </div>

            {/* Meeting információk */}
            {selectedAppointment.meetingLink && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Online Meeting
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <VideoCameraIcon className="h-4 w-4 text-green-600" />
                    <a
                      href={selectedAppointment.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 font-medium"
                    >
                      Csatlakozás a meetinghez
                    </a>
                  </div>
                  {selectedAppointment.meetingId && (
                    <div className="text-gray-600 dark:text-gray-400">
                      Meeting ID: {selectedAppointment.meetingId}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Üzenet */}
            {selectedAppointment.clientMessage && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Ügyfél üzenete
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedAppointment.clientMessage}
                </p>
              </div>
            )}

            {/* Akció gombok */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => handleEditAppointment(selectedAppointment)}
                className="flex items-center space-x-2"
              >
                <PencilSquareIcon className="h-4 w-4" />
                <span>Szerkesztés</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Bezárás
                </Button>
                {selectedAppointment.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        onUpdateStatus(selectedAppointment.id!, 'cancelled');
                        setShowDetailsModal(false);
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Elutasítás
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        onUpdateStatus(selectedAppointment.id!, 'confirmed');
                        setShowDetailsModal(false);
                      }}
                    >
                      Jóváhagyás
                    </Button>
                  </>
                )}
                {selectedAppointment.status === 'confirmed' && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onUpdateStatus(selectedAppointment.id!, 'completed');
                      setShowDetailsModal(false);
                    }}
                  >
                    Befejezés
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Szerkesztő Modal */}
      <AppointmentEditModal
        appointment={selectedAppointment}
        settings={settings}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAppointment(null);
        }}
        onSave={handleSaveAppointment}
      />
    </div>
  );
};

export default AppointmentList;
